import { formatDateTime } from '@/lib/utils'
import { colors, sxPresets } from '@/theme'
import { ArrowForward } from '@mui/icons-material'
import { Box, Paper, Typography } from '@mui/material'
import { useMemo } from 'react'
import OrderStatusBadge from './OrderStatusBadge'
import OrderSubscriptionTypeChip from './OrderSubscriptionTypeChip'
import CollapsibleCard from './CollapsibleCard'
import type { OrderListItem } from '@/types/order'
import { InlineLoadingSpinner } from '../common/LoadingSpinner'

interface PreviousOrdersListProps {
  orders: OrderListItem[]
  sectionId?: string
  title?: string
  onItemClick?: (hash: string) => void
  isLoading?: boolean
}

export default function PreviousOrdersList({
  orders,
  sectionId = 'orders',
  title = 'Orders',
  onItemClick,
  isLoading = false
}: PreviousOrdersListProps) {
  const ordersForList = useMemo(
    () =>
      orders.map(order => {
        // Get first product name for display
        const firstProductName = order.items[0]?.name || 'Unknown Product'

        // Get all unique category names across all products
        const allCategories = order.items
          .flatMap(product => product.categories)
          .map(cat => cat.name)
          .filter((name): name is string => name !== null)
        const uniqueCategories = [...new Set(allCategories)]
        const categoryDisplay = uniqueCategories.length > 0 ? uniqueCategories.join(', ') : 'Unknown'

        return {
          hash: order.id,
          name: firstProductName,
          status: order.status,
          date: new Date(order.createdAt),
          categoryDisplay,
          subscriptionType: order.type,
          orderNumber: order.orderNumber ?? 'N/A',
          numberOfExtraProducts: order.itemsCount - 1
        }
      }),
    [orders]
  )

  const isEmpty = !orders?.length

  const loadingContent = (
    <Box sx={{ ...sxPresets().section.content, p: 4, textAlign: 'center' }}>
      <InlineLoadingSpinner text='Loading orders...' />
    </Box>
  )

  return (
    <CollapsibleCard
      sectionId={sectionId}
      title={title}
      isEmpty={isEmpty || isLoading}
      emptyMessage='No orders found'
      emptyContent={isLoading ? loadingContent : undefined}
    >
      <Box
        sx={{
          ...sxPresets().section.content,
          bgcolor: colors.background.surface,
          ...sxPresets().grid.threeColumn,
          ...sxPresets(230).scrollable.twoRows
        }}
      >
        {ordersForList.map(order => (
          <Paper
            key={order.hash}
            onClick={() => onItemClick?.(order.hash)}
            elevation={0}
            sx={{
              ...sxPresets().card.default,
              ...(onItemClick ? sxPresets().card.clickable : {}),
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, pt: 2, pb: 1.5 }}>
              <Typography variant='smallText'>{order.categoryDisplay}</Typography>
              <OrderStatusBadge status={order.status} />
            </Box>

            <Box sx={{ px: 2, pb: 2, flex: 1 }}>
              <Typography variant='cardTitle' sx={{ mb: 1 }}>
                {order.name} {order.numberOfExtraProducts > 0 ? `+${order.numberOfExtraProducts}` : ''}
              </Typography>
              <Typography variant='smallText' sx={{ mb: 0.5 }}>
                {order.orderNumber}
              </Typography>
              <Typography variant='smallText' sx={{ mb: 0.5 }}>
                {formatDateTime(order.date)}
              </Typography>
              {order.subscriptionType && (
                <OrderSubscriptionTypeChip
                  subscriptionType={order.subscriptionType}
                  orderNumber={order.orderNumber}
                  sx={{ mt: 1 }}
                />
              )}
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                px: 2,
                py: 1.5,
                bgcolor: colors.brand.primaryLight
              }}
            >
              <Typography variant='link' sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                View order
                <ArrowForward sx={{ fontSize: '14px' }} />
              </Typography>
            </Box>
          </Paper>
        ))}
      </Box>
    </CollapsibleCard>
  )
}
